import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { chartColors, chartThemes } from "../theme";
import { useTheme } from "../themeContext";
import type { RtpSeriesPoint } from "./charts";

const CUMULATIVE_COLOR = chartColors.emerald;
const ROLLING_COLOR = chartColors.amber;

/**
 * RTP-over-spins line chart for the first N spins of a run.
 *
 * Two overlaid series, each toggleable:
 * - **Cumulative** — running RTP, converges toward the long-run value.
 * - **Rolling** — windowed RTP (last `window` spins), shows local variance.
 *
 * Data comes from a dedicated small raw-path pass (`adapter.sampleWinnings`)
 * on the same seed as the main run; counter-based Philox makes it
 * bit-identical to the main run's first N spins. See `computeRtpSeries`.
 */
export function RtpConvergencePanel({
  points,
  window,
}: {
  points: RtpSeriesPoint[];
  window: number;
}) {
  const [showCumulative, setShowCumulative] = useState(true);
  const [showRolling, setShowRolling] = useState(true);
  const { theme } = useTheme();
  const ct = chartThemes[theme];

  const totalSpins = points.at(-1)?.spin ?? 0;

  return (
    <section className="border border-paper-border p-4 dark:border-term-border">
      <header className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-widest text-paper-bright dark:text-term-bright">
            RTP convergence
          </h2>
          <p className="text-[10px] text-paper-hint dark:text-term-hint">
            running RTP over the first {totalSpins.toLocaleString()} spins (same
            seed)
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <SeriesToggle
            label="Cumulative"
            color={CUMULATIVE_COLOR}
            checked={showCumulative}
            onChange={setShowCumulative}
          />
          <SeriesToggle
            label={`Rolling (${window.toLocaleString()})`}
            color={ROLLING_COLOR}
            checked={showRolling}
            onChange={setShowRolling}
          />
        </div>
      </header>

      {points.length > 0 ? (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart
            data={points}
            margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} />
            <XAxis
              dataKey="spin"
              type="number"
              domain={[1, "dataMax"]}
              scale="log"
              tick={ct.tick}
              axisLine={{ stroke: ct.axis }}
              tickLine={{ stroke: ct.axis }}
              tickFormatter={formatSpin}
            />
            <YAxis
              width={52}
              tick={ct.tick}
              axisLine={{ stroke: ct.axis }}
              tickLine={{ stroke: ct.axis }}
              tickFormatter={(v: number) => `${v.toFixed(0)}%`}
            />
            <Tooltip
              labelFormatter={(label) =>
                `spin ${typeof label === "number" ? label.toLocaleString() : label}`
              }
              formatter={(value, name) => [
                typeof value === "number"
                  ? `${value.toFixed(2)}%`
                  : String(value),
                name === "cumulative" ? "Cumulative" : "Rolling",
              ]}
              contentStyle={ct.tooltipContentStyle}
              labelStyle={ct.tooltipLabelStyle}
              itemStyle={ct.tooltipItemStyle}
            />
            {showCumulative && (
              <Line
                type="monotone"
                dataKey="cumulative"
                name="cumulative"
                stroke={CUMULATIVE_COLOR}
                dot={false}
                strokeWidth={2}
                isAnimationActive={false}
              />
            )}
            {showRolling && (
              <Line
                type="monotone"
                dataKey="rolling"
                name="rolling"
                stroke={ROLLING_COLOR}
                dot={false}
                strokeWidth={1}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex h-[260px] items-center justify-center text-xs text-paper-hint dark:text-term-hint">
          No data yet
        </div>
      )}
    </section>
  );
}

function SeriesToggle({
  label,
  color,
  checked,
  onChange,
}: {
  label: string;
  color: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5 select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-emerald-700 dark:accent-emerald-500"
      />
      <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: color }} />
      <span className="text-paper-label dark:text-term-label">{label}</span>
    </label>
  );
}

/** Compact spin-count axis tick: 1, 100, 1k, 10k. */
function formatSpin(v: number): string {
  if (v >= 1_000_000) return `${v / 1_000_000}M`;
  if (v >= 1_000) return `${v / 1_000}k`;
  return `${v}`;
}
